/** The one way this application talks to its API.
 *
 * Every fetch goes through here so that three things happen consistently
 * rather than per screen:
 *
 *   - the access token is attached
 *   - a 401 ends the session and says WHY (UI_TASTE.md: never drop the user
 *     at a blank login screen)
 *   - a failure surfaces a real message. BUILD_GUIDE.md's error-handling
 *     standard exists because LEGACY_ANALYSIS.md §8 found 35 places where the
 *     old system carried on as though nothing had happened.
 */

import { isMockMode, mockRequest } from './mock'

declare global {
  interface Window { FKMT_CONFIG?: { apiUrl: string } }
}

const API_URL = window.FKMT_CONFIG?.apiUrl ?? '/api'

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly conflict = false,
  ) {
    super(message)
  }
}

type SignOutReason = 'expired' | 'idle' | 'suspended' | 'other'
let onSignOut: (reason: SignOutReason) => void = () => {}
export function setSignOutHandler(fn: (r: SignOutReason) => void) {
  onSignOut = fn
}

/** Called on every 403. The rights list refreshes, so a right revoked while
 *  the screen was open takes its button with it at once. RIGHTS_MODEL §6.1. */
let onForbidden: () => void = () => {}
export function setForbiddenHandler(fn: () => void) {
  onForbidden = fn
}

/** The access token, kept in sessionStorage so a page refresh does not sign
 *  the user out — reloading a page is not a request to be logged out.
 *
 *  sessionStorage rather than localStorage, deliberately: it survives a
 *  refresh and dies when the tab closes, so a shared machine does not carry
 *  a live session into the next person's browsing.
 *
 *  ONLY the token is kept. Company, financial year and rights are never
 *  stored in the browser — those are the server's answers, and a copy here is
 *  a copy that can be edited. The app asks `GET /auth/session` on load and
 *  rebuilds from what comes back. The server still governs the session: 30
 *  minutes idle, 8 hours absolute, revocable at any moment. */
const TOKEN_KEY = 'fkmt.erp.token'

let token: string | null = readToken()

function readToken(): string | null {
  try { return sessionStorage.getItem(TOKEN_KEY) } catch { return null }
}

export function setToken(t: string | null) {
  token = t
  // Wrapped: a browser with site data blocked throws here, and failing to
  // remember a token must never stop somebody signing in.
  try {
    if (t) sessionStorage.setItem(TOKEN_KEY, t)
    else sessionStorage.removeItem(TOKEN_KEY)
  } catch { /* the session simply will not survive a refresh */ }
}

export function hasStoredToken(): boolean { return token !== null }

/** Repeat identical creates are swallowed while one is still in flight, so
 *  an impatient triple-click on a slow network cannot persist the same
 *  record three times. UI_TASTE.md. */
const inFlight = new Map<string, Promise<unknown>>()

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  query?: Record<string, string | undefined>,
): Promise<T> {
  // No API configured means `npm run dev` with no backend yet. Sample data
  // stands in, and App.tsx labels it so it cannot be mistaken for live data.
  if (isMockMode()) {
    try {
      return await mockRequest(method, path, body as Record<string, unknown>,
                               query) as T
    } catch (e) {
      const status = (e as { status?: number }).status ?? 400
      throw new ApiError((e as Error).message, status, status === 409)
    }
  }

  const url = new URL(`${API_URL}${path}`, window.location.origin)
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== undefined && v !== '') url.searchParams.set(k, v)
  }

  const res = await fetch(url.toString(), {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  let payload: { error?: string } = {}
  try { payload = await res.json() } catch { /* empty body is fine */ }

  if (res.status === 401) {
    onSignOut('expired')
    // The server's own words, when it has any. Replacing every 401 with
    // "your session has ended" hid the real reason for hours: the actual
    // message was "You do not have access to that company and financial
    // year", which would have pointed straight at the cause. A generic
    // sentence is the fallback for a 401 with no body, not the default.
    throw new ApiError(
      payload.error ?? 'Your session has ended. Please sign in again.', 401)
  }

  if (res.status === 403) onForbidden()

  if (!res.ok) {
    throw new ApiError(
      payload.error ?? `The request failed (${res.status}).`,
      res.status,
      res.status === 409,
    )
  }
  return payload as T
}

export const api = {
  get: <T>(path: string, query?: Record<string, string | undefined>) =>
    request<T>('GET', path, undefined, query),

  create: <T>(path: string, body: unknown) => {
    const key = `${path}:${JSON.stringify(body)}`
    const running = inFlight.get(key)
    if (running) return running as Promise<T>
    const p = request<T>('POST', path, body).finally(() => inFlight.delete(key))
    inFlight.set(key, p)
    return p
  },

  update: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  patch:  <T>(path: string, body: unknown) => request<T>('PATCH', path, body),

  // Deliberately rare. Business records are never deleted — decision 18, and
  // the application role holds no DELETE on any of them. This exists for
  // CONFIGURATION: a rights grant, a route operation. Anything that states
  // what is true now rather than what happened.
  remove: <T>(path: string, query?: Record<string, string | undefined>) =>
    request<T>('DELETE', path, undefined, query),
}
